import formatUtils from '~/utils/formatUtils';
import type { ITeaser } from '~~/types/local_types';
import collectionsConfig from '~~/configs/collections.config';

/**
 * This composable contains functions to get teasers from the Directus API.
 */
const useTeasers = () => {
  const cms = useCms();
  const { objectKeysToCamelCase } = formatUtils();
  const countLimit = 240; // this is the max number of items a load more grid can contain
  /**
   * Fetch the count of teasers based on a filter (capped at countLimit).
   * Cached in the payload per collection + filter, so hydration reuses the SSR count.
   */
  const getCount = async (filter: object, collection = 'articles'): Promise<number> => {
    const cachedCount = useNuxtData<number>(`count-${collection}-${JSON.stringify(filter)}`).data;
    if (typeof cachedCount.value === 'number') return cachedCount.value;

    try {
      const [row] = await cms.aggregate<{ countDistinct?: { id?: number | string } }>(collection, {
        filter,
      });
      const count = Math.min(Number(row?.countDistinct?.id) || 0, countLimit);
      cachedCount.value = count;
      return count;
    } catch (error) {
      console.warn('Error fetching teaser count:', error);
      return 0;
    }
  };

  /**
   * Fetch teasers directly from Directus.
   */
  const getDirectusData = async (
    fields: Array<string | object>,
    offset: number,
    filter: object,
    sort: string,
    limit: number,
    collection = 'articles',
  ): Promise<ITeaser[]> => {
    try {
      const teasers = await cms.readItems(collection, {
        fields,
        offset,
        filter,
        sort: sort || '-publish_time',
        limit: limit ?? 240,
      });
      return objectKeysToCamelCase(teasers) as ITeaser[];
    } catch (error) {
      console.warn('Error fetching teasers:', error);
      return [];
    }
  };

  /**
   * Get cached teasers for a given cache key.
   */
  const getCachedTeasers = (cacheKey: string): ITeaser[] => {
    // this invalidates cache, which forces reload of items (in the client), this creates blank sections (with missing items, while they load)
    // cacheKey = getTimedCacheKey(cacheKey);
    return useNuxtData(cacheKey).data.value || [];
  };

  // invalidate frontend cache after 5 minutes
  // const getTimedCacheKey = (baseKey: string): string => {
  //   const now = Date.now();
  //   const timeoutTime = 5 * 60 * 1000; // 5 minutes
  //   const rounded = Math.floor(now / timeoutTime) * timeoutTime;
  //   return `${baseKey}-${rounded}`;
  // };

  /**
   * Get teasers, either from cache or Directus.
   */
  const getTeasers = async (
    limit: number,
    filter: object,
    cacheKey: string,
    sort: string,
    offset = 0,
    loadMore = false,
    collection = 'articles',
    useCache = true,
  ): Promise<ITeaser[]> => {
    // this invalidates cache, which forces reload of items (in the client), this creates blank sections (with missing items, while they load)
    // cacheKey = getTimedCacheKey(cacheKey);

    const cachedData = useNuxtData(cacheKey);
    const teasers = useCache ? cachedData.data.value || [] : [];

    let newTeasers: ITeaser[] = [];

    const fieldsKey = collection === 'articles' ? 'teaser' : `${collection}Teaser`;
    const fields = collectionsConfig[fieldsKey] || ['*'];

    if (loadMore || !teasers.length) {
      newTeasers = await getDirectusData(fields, offset, filter, sort, limit, collection);
    }

    if (newTeasers.length) {
      cachedData.data.value = [...(cachedData.data.value || []), ...newTeasers];
    }

    if (loadMore) {
      return newTeasers;
    } else {
      return cachedData.data.value;
    }
  };

  return {
    getTeasers,
    getCount,
    getCachedTeasers,
  };
};

export default useTeasers;
